import {Expose, Transform} from "class-transformer";
import {DateTime} from "luxon";
import {container} from "tsyringe";
import {EmployeeRepository} from "../repositories/employee.repository";
import {Employee} from "./employee.entity";
import {Entity} from "./entity";
import {Public} from "../../decorators/public.decorator";
import {formatPersonName} from "../helpers/format-person-name.helper";

/**
 * A user account as the frontend reads it from `/api/v1/query/user`.
 *
 * Loaded through the generic `Repository._transformDtoToEntity` path, which wraps it in a
 * `ChangeTracker` proxy AFTER the stored values are set, so a fresh load has no changed keys and
 * `update.user` sends only the names that really changed (ADR-015). The constructor must therefore
 * NOT wrap itself: a proxy created before the values arrive counts every loaded field as changed.
 *
 * Only `firstName` and `lastName` are tracked (`@Public()`). `emailAddress` is read-only here: an
 * e-mail change needs the current password and goes through `change.own-email-address`.
 */
export class User extends Entity<User> {

    private _deletedAt: DateTime | null = null;

    private _emailAddress: string = ``;

    private _employee: string | null = null;

    private _employeePromise: Promise<Employee | null> | null = null;

    private _firstName: string = ``;

    private _lastName: string = ``;

    private _username: string;

    @Expose()
    @Transform(({value}) => value ? DateTime.fromSQL(value) : null, {toClassOnly: true})
    public get deletedAt(): DateTime | null {
        return this._deletedAt;
    }

    public set deletedAt(value: DateTime | null) {
        this._deletedAt = value;
    }

    @Expose()
    public get emailAddress(): string {
        return this._emailAddress;
    }

    public set emailAddress(value: string) {
        this._emailAddress = value;
    }

    public get employee(): Promise<Employee | null> {
        if (!this._employee) {
            return Promise.resolve(null);
        }

        if (!this._employeePromise) {
            this._employeePromise = container.resolve(EmployeeRepository).findOneById(this._employee);
        }

        return this._employeePromise;
    }

    /** The DTO calls the employee link `employee`; the getter of that name resolves the entity. */
    @Expose({name: `employee`, toClassOnly: true})
    public set employeeUid(value: string | null) {
        this._employee = value;
    }

    @Public()
    @Expose()
    public get firstName(): string {
        return this._firstName;
    }

    public set firstName(value: string) {
        this._firstName = value;
    }

    public get isDeleted(): boolean {
        return this._deletedAt instanceof DateTime;
    }

    @Public()
    @Expose()
    public get lastName(): string {
        return this._lastName;
    }

    public set lastName(value: string) {
        this._lastName = value;
    }

    @Expose()
    public get username(): string {
        return this._username;
    }

    public set username(value: string) {
        this._username = value;
    }

    constructor(username: string = ``) {
        super();
        this._username = username;
    }

    /** "Nachname, Vorname (username)"; a missing name part drops its comma (see formatPersonName). */
    public toString(): string {
        const name = formatPersonName(this._lastName, this._firstName);
        return name === '' ? `(${this._username})` : `${name} (${this._username})`;
    }

}